// The unit detail as a full page (redesign U1, decisions D6 / D7): its
// route, the tab in the address, where "Back" goes, the inventory view the
// user came from (restored on the way back) and the list prev / next walks.
//
// The route is /unit/:stock?tab=<id>. Tab ids are today's UnitDetailPane ids,
// so every existing entry point (cell tabs, preview links, Task Board,
// Branches) maps 1:1. What is NOT in the address travels in the history
// entry's state: the page to return to, the ordered stock list of the view
// and its filter pills. A copied link therefore carries the unit and the tab
// only (U3: filter context stripped).

import { buildFilterPills, RISK_TIER_LABELS } from './inventoryView';
import { verdictLabel } from './formatting';

export const UNIT_ROUTE = '/unit/:stock';

// Today's tabs, in order, with their labels (P-UNIT-012).
export const UNIT_TABS = Object.freeze([
    { id: 'ai', label: 'Intelligence' },
    { id: 'fasttrack', label: 'Ask IronSight' },
    { id: 'overview', label: 'Overview' },
    { id: 'service', label: 'Service History' },
    { id: 'photos', label: 'Photos' },
    { id: 'pricing', label: 'Pricing & Usage' },
    { id: 'pipeline', label: 'Pipeline' },
    { id: 'comps', label: 'Comps' },
    { id: 'activity', label: 'Activity' },
]);
export const DEFAULT_UNIT_TAB = 'ai';
const TAB_IDS = UNIT_TABS.map((t) => t.id);
// The prototype's ids, accepted on the way in.
const TAB_ALIASES = Object.freeze({ ask: 'fasttrack', intelligence: 'ai' });

export const unitTabLabel = (id) => UNIT_TABS.find((t) => t.id === id)?.label || UNIT_TABS[0].label;

/** A known tab id, or the default for anything else. */
export const normalizeUnitTab = (value) => {
    const id = String(value || '').toLowerCase();
    const resolved = TAB_ALIASES[id] || id;
    return TAB_IDS.includes(resolved) ? resolved : DEFAULT_UNIT_TAB;
};

/** The tab named by a query string (`?tab=`), or the default. */
export const tabFromSearch = (search) => {
    try { return normalizeUnitTab(new URLSearchParams(search || '').get('tab')); } catch (e) { return DEFAULT_UNIT_TAB; }
};

/** /unit/<stock>?tab=<tab> */
export const unitPath = (stock, tab = DEFAULT_UNIT_TAB) => (
    `/unit/${encodeURIComponent(String(stock))}?tab=${normalizeUnitTab(tab)}`
);

/** The shareable address of a unit on a tab (U3): origin + path, nothing else. */
export const unitShareUrl = (stock, tab, origin = (typeof window !== 'undefined' ? window.location.origin : '')) => (
    `${origin}${unitPath(stock, tab)}`
);

// ── Where "Back" goes (D7) ──────────────────────────────────────────────

export const FROM_LABELS = Object.freeze({ inventory: 'Inventory', tasks: 'Tasks', branches: 'Branches' });

/** The page a location belongs to: inventory, tasks or branches. */
export const fromKind = (pathname) => {
    if (pathname === '/tasks') return 'tasks';
    if (pathname === '/branches') return 'branches';
    return 'inventory';
};

/** A plain, serialisable "came from" record for history state. */
export const fromLocation = (location) => ({
    pathname: location?.pathname || '/',
    search: location?.search || '',
    hash: location?.hash || '',
    kind: fromKind(location?.pathname),
});

/** "Back to inventory" / "Back to tasks" / "Back to branches". */
export const backLabel = (from) => `Back to ${(FROM_LABELS[from?.kind] || FROM_LABELS.inventory).toLowerCase()}`;

// ── The inventory view the user left (restored on return) ──────────────
//
// Kept in memory only (not storage): it is the view of this page load. It is
// stamped with the inventory history entry's key, so it is restored exactly
// when that entry comes back (Back, the browser's back button), never when the
// inventory is opened fresh from the nav.

let inventorySnapshot = null;

const entryKey = (location) => location?.key || `${location?.pathname || '/'}${location?.search || ''}`;

export function saveInventorySnapshot(location, view) {
    inventorySnapshot = { key: entryKey(location), ...view };
}

/** The snapshot for this inventory entry, once; null for any other entry. */
export function takeInventorySnapshot(location) {
    if (!inventorySnapshot || inventorySnapshot.key !== entryKey(location)) return null;
    const snap = inventorySnapshot;
    inventorySnapshot = null;
    return snap;
}

/** Tests only. */
export function __resetInventorySnapshotForTests() { inventorySnapshot = null; }

// ── Filter context for the pills on the unit page (U2) ─────────────────

const SORT_LABELS = Object.freeze({
    StockNumber: 'Stock #',
    MAKE: 'Equipment',
    BranchName: 'Branch',
    DaysInStock: 'Days',
    Best_Sell_Price: 'Price',
    AiPriceGap: 'AI gap',
    CarryingCostBasis: 'True cost',
    LadenhueterRisk: 'Risk',
    QuoteCount: 'Interest',
    LastQuotedGapPct: 'Last quote',
    ODOMETER: 'Hours',
    DealStatus: 'Deal status',
});

/**
 * The inventory view as pills: one per active filter (the inventory's own
 * pill labels), the signals, Open Tasks, Live quote / Retarget, and the sort
 * when it is not the default. Plain { label, value } records.
 */
export function inventoryContextPills({
    filters, anomalyFilter, taskFilterActive, sort, defaultSort, machineCatalog, agingBuckets,
}) {
    const pills = (filters ? buildFilterPills(filters, machineCatalog, agingBuckets) : [])
        .map((p) => ({ label: p.label, value: p.value }));
    const verdicts = anomalyFilter ? [...anomalyFilter] : [];
    if (verdicts.length) {
        pills.push({
            label: 'Signals',
            value: verdicts.length > 3 ? `${verdicts.length} signals` : verdicts.map(verdictLabel).join(', '),
        });
    }
    if (filters?.qualifyingQuote) pills.push({ label: 'Live quote', value: 'on' });
    if (filters?.retargeting) pills.push({ label: 'Retarget', value: 'on' });
    if (taskFilterActive) pills.push({ label: 'Open Tasks', value: 'on' });
    if (sort && defaultSort && (sort.key !== defaultSort.key || sort.direction !== defaultSort.direction)) {
        pills.push({
            label: 'Sorted by',
            value: `${SORT_LABELS[sort.key] || sort.key} ${sort.direction === 'asc' ? '↑' : '↓'}`,
        });
    }
    return pills;
}

/**
 * The view name shown as "from <b>…</b>": "Filtered inventory" only when
 * something filters the list; a sort alone is still the whole "Inventory"
 * (its "Sorted by" pill follows).
 */
export const contextViewName = (pills) => (
    pills.some((p) => p.label !== 'Sorted by') ? 'Filtered inventory' : 'Inventory'
);

// Re-exported so callers building pills elsewhere read the same labels.
export { RISK_TIER_LABELS };

// ── Prev / next (U2) ────────────────────────────────────────────────────

/** Position of `stock` in `stocks` with its neighbours (null at the ends). */
export function stepperPosition(stocks, stock) {
    const list = Array.isArray(stocks) ? stocks.map(String) : [];
    const index = list.indexOf(String(stock));
    if (index === -1) return { index: -1, total: list.length, prev: null, next: null };
    return {
        index,
        total: list.length,
        prev: index > 0 ? list[index - 1] : null,
        next: index < list.length - 1 ? list[index + 1] : null,
    };
}

// ── The Task Board view the user left (restored on return, D7) ─────────
// Same rules as the inventory snapshot: memory only, stamped with the board's
// history entry, taken once.

let tasksSnapshot = null;

export function saveTasksSnapshot(location, view) {
    tasksSnapshot = { key: entryKey(location), ...view };
}

/** The snapshot for this Task Board entry, once; null for any other entry. */
export function takeTasksSnapshot(location) {
    if (!tasksSnapshot || tasksSnapshot.key !== entryKey(location)) return null;
    const snap = tasksSnapshot;
    tasksSnapshot = null;
    return snap;
}

/** Tests only. */
export function __resetTasksSnapshotForTests() { tasksSnapshot = null; }
